import { useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Copy } from "lucide-react";

export function InviteMember({ onInvited }) {
  const [form, setForm] = useState({ name: "", email: "", role: "host" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invite", form);
      setResult(data);
      toast.success(data.email_sent ? `Invitation envoyée à ${form.email}` : "Compte créé, mais l'email n'a pas pu partir : transmettez le mot de passe ci-dessous");
      setForm({ name: "", email: "", role: "host" });
      onInvited?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-border p-5" data-testid="invite-member">
      <h2 className="font-serif text-2xl text-olive flex items-center gap-2"><UserPlus className="w-5 h-5 text-rose-brand" /> Inviter un membre de l'équipe</h2>
      <p className="text-sm text-muted-foreground mt-1">Un mot de passe temporaire est généré et envoyé par email ; la personne devra le changer à sa première connexion.</p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 items-end">
        <div><Label className="text-xs">Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
        <div><Label className="text-xs">Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
        <div>
          <Label className="text-xs">Rôle</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="host">Hôte / équipe</SelectItem><SelectItem value="admin">Administrateur</SelectItem></SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={busy} className="bg-olive text-white" data-testid="invite-submit">{busy ? "..." : "Inviter"}</Button>
      </form>
      {result && (
        <div className="mt-4 p-3 rounded-xl bg-secondary text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="invite-result">
          <span>Compte créé pour <strong>{result.user.email}</strong> — mot de passe temporaire : <code className="px-2 py-0.5 bg-white rounded border border-border" data-testid="invite-temp-password">{result.temp_password}</code>{!result.email_sent && <span className="text-rose-brand ml-2">(email non envoyé)</span>}</span>
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(result.temp_password); toast.success("Copié"); }}><Copy className="w-3.5 h-3.5 mr-1" /> Copier</Button>
        </div>
      )}
    </div>
  );
}
